<script>
	import '../app.css';
	import { browser } from '$app/environment';
	import { afterNavigate, beforeNavigate, goto } from '$app/navigation';
	import { Analytics } from '$lib/services/analytics/client';
	import { closeAllModals } from 'svelte-modals';
	import { onMount, onDestroy } from 'svelte';
	import GlobalLoadingOverlay from '$lib/services/loading/GlobalLoadingOverlay.svelte';
	import { loadThirdPartyScripts } from '$lib/utils/third-party.js';
	import { AOFL_PRODUCT_NAMES, PAGES } from '$lib/utils/constants';
	import { get } from 'svelte/store';
	import { app } from '$lib/stores/app/store';
	import { getExperiments } from '$lib/stores/experiments/store';
	import {
		canLoadMarketingVendors,
		initializeAnalyticsCookiesStore,
	} from '$lib/stores/app/actions';
	import { hotjarService } from '$lib/services/hotjar';
	import { AnalyticsService } from '$lib/services/analytics';
	import { ProductExperienceService } from '$lib/services/product-experience';
	import { AppInitializationService } from '$lib/services/app-initialization';
	import {
		ABC_ATTRIBUTES,
		Sentry,
		recordRouteStart,
		setExperimentRouteAttributes,
		setRouteAttributes,
	} from '$lib/services/sentry';

	// SvelteKit route id for src/routes/inbound-gateway (route ids never carry
	// the `/abc` kit.paths.base) — same literal used by the sampler's funnel
	// tier in $lib/services/sentry/config.ts.
	const INBOUND_GATEWAY_ROUTE_ID = '/inbound-gateway';

	// Nav lineage counter for `abc.nav_index`. Component-scope (not per-call),
	// so it accumulates across every navigation for the life of the session.
	let navIndex = 0;

	beforeNavigate((navigation) => {
		Analytics.updatePreviousPageBeforeNavigate(navigation);
		// Timing baseline only. `beforeNavigate` fires before SvelteKit sets the
		// `navigating` store, which is what Sentry starts the navigation span from,
		// so there is no transaction to attach attributes to yet.
		recordRouteStart();
	});

	afterNavigate(async (nav) => {
		AnalyticsService.resetPageScrollTracking();

		const prevPage = nav?.from?.route?.id;
		const nextPage = nav?.to?.route?.id;
		const isInitialNavigation = nav?.type === 'enter';

		// SvelteKit sets `type: 'enter'` on every boot path, including ones where
		// `nav.from` is a truthy-but-empty object (route.id: null) rather than
		// null — pure SPA-mode boots (ssr:false, prerender:false, e.g.
		// /inbound-gateway) construct `from` that way even on the very first
		// navigation. Detecting the initial navigation via `nav.type` instead of
		// `Boolean(nav.from)` keeps nav_index at 0 on both boot shapes; every
		// navigation after that has `nav.type !== 'enter'` and increments it.
		if (!isInitialNavigation) navIndex += 1;

		const appState = get(app);

		// Runs for the initial pageload as well as SPA navigations, and by now the
		// transaction exists, so attributes land on it rather than the previous one.
		//
		// The IN_APP / LINK_FROM_APP_PLATFORM / PRODUCT_EXPERIENCE /
		// IS_FIRST_TIME_VISIT / experiment boot attrs are also promoted to
		// root inside AppInitializationService.initialize(), but that runs
		// exactly once per hard pageload (SvelteKit's client init() hook) —
		// never on SPA navigations. Re-stamping them here from the app and
		// experiments stores on every afterNavigate keeps them present on every
		// navigation transaction, including the alt-payment cohort's gateway ->
		// destination hop, which is a pure SPA nav that init() never re-runs for.
		//
		// INBOUND_ENTRY is naturally one-shot: `prevPage` only equals the
		// gateway's route id on the single navigation immediately following its
		// goto() redirect. A user who somehow navigates from the gateway more
		// than once in a session (e.g. back button) would set it again, which is
		// still semantically correct — each such nav genuinely did just arrive
		// from the gateway — so no extra guard is needed.
		setExperimentRouteAttributes(getExperiments());
		setRouteAttributes({
			[ABC_ATTRIBUTES.IN_APP]: Boolean(appState?.meta?.inApp),
			[ABC_ATTRIBUTES.INBOUND_ENTRY]: prevPage === INBOUND_GATEWAY_ROUTE_ID ? 'true' : undefined,
			[ABC_ATTRIBUTES.IS_FIRST_TIME_VISIT]: Boolean(appState?.global?.isFirstTimeVisit),
			[ABC_ATTRIBUTES.LINK_FROM_APP_PLATFORM]: String(
				appState?.global?.isLinkFromAppPlatform ?? 'none'
			),
			[ABC_ATTRIBUTES.NAV_FROM]: isInitialNavigation ? undefined : prevPage,
			[ABC_ATTRIBUTES.NAV_INDEX]: navIndex,
			[ABC_ATTRIBUTES.PRODUCT_EXPERIENCE]: ProductExperienceService.AOFL_PRODUCT_NAME,
			[ABC_ATTRIBUTES.ROUTE_ID]: nextPage ?? nav?.to?.url?.pathname,
		});

		Sentry.addBreadcrumb({
			category: 'navigation',
			message: `Navigated from ${prevPage} to ${nextPage}`,
			level: 'info',
		});

		// `init` already records the entry URL. Calling stateChange on `enter`
		// would duplicate that pageview; SPA hops are the ones Hotjar would
		// otherwise keep attributing to the first path.
		if (!isInitialNavigation && nav?.to?.url?.pathname) {
			hotjarService.stateChange(nav.to.url.pathname);
		}
	});

	/**
	 * This function is used to redirect the user to the download app page if the user is a ABC Mouse App 2.0 user trying to access to the ABCMouse App 1.0
	 * Only redirects in specific scenarios, not on every page load
	 * TODO: needs to be refactored to move into the post auth service.
	 * @returns {void}
	 */
	function abc2DownloadAppInterceptor() {
		const isAbcMouse2_0Product = get(app).global.appProduct === AOFL_PRODUCT_NAMES.ABCMOUSE_2_0;
		const currentPath = window.location.pathname;
		const isAlreadyThere = currentPath === PAGES.INTERNAL.DOWNLOAD_APP;

		// Don't redirect if user is already on download-app page or not a 2.0 product
		if (isAlreadyThere || !isAbcMouse2_0Product) return;

		// Only redirect if user is on a path that should not be accessible for 2.0 users
		// For now, we'll be less aggressive and let users navigate normally
		// The RegistrationPathService will handle proper flow after login/subscription
		const shouldRedirect = false; // Disable automatic redirects for now

		if (shouldRedirect) {
			goto(PAGES.INTERNAL.DOWNLOAD_APP);
		}
	}

	/**
	 *
	 */
	function loadPerimeterX() {
		const s = document.createElement('script');
		s.src = '//client.perimeterx.net/PXTU0h5FRQ/main.min.js';
		s.async = true;
		document.head.appendChild(s);
	}

	onMount(async () => {
		if ($app?.meta?.isAmazonApp || $app?.meta?.isAndroidApp)
			document.documentElement.classList.add('fixed-font');

		initializeAnalyticsCookiesStore();
		AnalyticsService.onMount();

		window.addEventListener('pageshow', (e) => {
			if (e.persisted) closeAllModals();
		});

		if (canLoadMarketingVendors()) {
			loadThirdPartyScripts();
		}

		// Self-gates on consent and environment, so it runs unconditionally here.
		hotjarService.initialize();

		// This is a custom interceptor to redirect to the download app page if the user is a ABC Mouse App 2.0 user trying to access to the ABCMouse App 1.0
		abc2DownloadAppInterceptor();

		if (typeof requestIdleCallback !== 'undefined') {
			requestIdleCallback(() => loadPerimeterX());
		} else {
			setTimeout(() => loadPerimeterX(), 2000);
		}

		if (!$app?.meta?.inApp) {
			void AppInitializationService.warmPaymentSdks();
		}
	});

	onDestroy(() => {
		if (browser) AnalyticsService.onDestroy();
	});

	const appVersion = __APP_VERSION__;
</script>

<!-- TODO: move this to be config driven: debug build -->
<svelte:head>
	<meta
		name="app-version"
		content={appVersion}
	/>
	<script></script>
	<!-- <script src="https://seo-games-abc-mouse.ngrok.dev/vorlon.js"></script> -->
</svelte:head>

<svelte:window on:scroll|passive={AnalyticsService.scrollTrackingHandler} />

<slot />

<GlobalLoadingOverlay />
